import React from 'react';
import { ArrowRight, Star, MapPin, Banknote, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
export function FeaturedProjects() {
  const featured = {
    category: 'Trade & Investment',
    title: 'Africa Global Trade Partnership Initiative',
    description: 'Strengthening trade relationships and facilitating investment flows between African nations and global markets through capacity building, trade missions, and regulatory harmonization in support of AfCFTA.',
    status: 'Active',
    countries: 'Kenya, Nigeria, South Africa, Ghana',
    budget: '£15,000,000',
    timeline: 'Jan 2025 – Dec 2027'
  };
  const projects = [{
    id: 1,
    title: 'Pan-African Health Initiative',
    category: 'Health',
    description: 'Strengthening healthcare infrastructure and disease response capabilities across 15 member states through targeted investments and capacity building.',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=800'
  }, {
    id: 2,
    title: 'Continental Free Trade Support',
    category: 'Education & Trade',
    description: 'Facilitating cross-border commerce and providing vocational training to empower youth and women entrepreneurs in emerging markets.',
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=800'
  }, {
    id: 3,
    title: 'Sustainable Energy Corridors',
    category: 'Infrastructure',
    description: 'Developing renewable energy grids and transportation networks to connect landlocked regions and promote sustainable industrialization.',
    image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&q=80&w=800'
  }];
  const metaRows = [{
    icon: MapPin,
    label: 'Countries',
    value: featured.countries,
    valueClass: 'text-gray-900 font-medium'
  }, {
    icon: Banknote,
    label: 'Total Budget',
    value: featured.budget,
    valueClass: 'text-au-green font-bold'
  }, {
    icon: Calendar,
    label: 'Timeline',
    value: featured.timeline,
    valueClass: 'text-gray-900 font-medium'
  }];
  return <section id="projects" className="py-20 bg-gray-50" data-id="element-77">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-78">
        {/* Featured Project */}
        <div className="flex items-center gap-2 mb-6" data-id="element-79">
          <Star size={20} className="text-au-gold fill-au-gold" data-id="element-80" />
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-au-navy" data-id="element-81">
            Featured Project
          </h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 lg:grid-cols-2 rounded-lg overflow-hidden border border-gray-200 shadow-sm mb-16" data-id="element-82">
          {/* Left: green panel */}
          <div className="bg-au-green text-white p-8 md:p-10" data-id="element-83">
            <span className="inline-block bg-au-gold text-au-navy text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-6" data-id="element-84">
              {featured.category}
            </span>
            <h3 className="text-2xl md:text-3xl font-serif font-bold leading-tight mb-4" data-id="element-85">
              {featured.title}
            </h3>
            <div className="h-1 w-16 bg-au-gold mb-6" data-id="element-86"></div>
            <p className="text-green-50 leading-relaxed" data-id="element-87">
              {featured.description}
            </p>
          </div>

          {/* Right: white meta panel */}
          <div className="bg-white p-8 md:p-10 flex flex-col justify-center" data-id="element-88">
            <span className="inline-block self-start bg-green-100 text-au-green text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-8" data-id="element-89">
              {featured.status}
            </span>
            <ul className="space-y-6" data-id="element-90">
              {metaRows.map((row, index) => {
              const Icon = row.icon;
              return <li key={index} className="flex items-start" data-id="element-91">
                    <span className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-92">
                      <Icon size={18} className="text-au-green" data-id="element-93" />
                    </span>
                    <div data-id="element-94">
                      <p className="text-sm text-gray-500 mb-0.5" data-id="element-95">
                        {row.label}
                      </p>
                      <p className={row.valueClass} data-id="element-96">{row.value}</p>
                    </div>
                  </li>;
            })}
            </ul>
          </div>
        </motion.div>

        {/* All Projects */}
        <div className="flex justify-between items-end mb-8" data-id="element-97">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-au-navy" data-id="element-98">
            All Projects
          </h2>
          <a href="#" className="hidden md:flex items-center text-au-green font-medium hover:text-au-navy transition-colors focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2 rounded-sm" data-id="element-99">
            View all projects <ArrowRight size={16} className="ml-1" data-id="element-100" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-101">
          {projects.map((project, index) => <motion.div key={project.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col" data-id="element-102">
              <div className="h-48 overflow-hidden relative" data-id="element-103">
                <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-104" />
                <div className="absolute top-4 left-4 bg-au-gold text-au-navy text-xs font-bold px-2 py-1 rounded uppercase tracking-wider" data-id="element-105">
                  {project.category}
                </div>
              </div>
              <div className="p-6 flex flex-col flex-grow" data-id="element-106">
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight" data-id="element-107">
                  {project.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-108">
                  {project.description}
                </p>
                <a href="#" className="inline-flex items-center text-au-green font-medium text-sm hover:text-au-navy transition-colors mt-auto focus:outline-none focus:ring-2 focus:ring-au-green focus:ring-offset-2 rounded-sm" data-id="element-109">
                  Learn more <ArrowRight size={14} className="ml-1" data-id="element-110" />
                </a>
              </div>
            </motion.div>)}
        </div>

        <div className="mt-8 text-center md:hidden" data-id="element-111">
          <a href="#" className="inline-flex items-center text-au-green font-medium hover:text-au-navy transition-colors" data-id="element-112">
            View all projects <ArrowRight size={16} className="ml-1" data-id="element-113" />
          </a>
        </div>
      </div>
    </section>;
}